import React, { useMemo } from 'react';
import { Avatar, Card, Skeleton, Tabs, Typography, theme } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../../pages/subscriptions/SubscriptionsPage';
import { formatCallNumber, renderDate, renderDevice, renderPlatform } from './callCells';
import type { TopSubscriptionEntry, TopSubscriptions } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';
import { useOverviewAvatars } from '../../../hooks/leedflow/useOverviewAvatars';

const { useToken } = theme;

interface TopSubscriptionsPanelProps {
  rows: TopSubscriptions;
  status: ContextStatus;
}

type Dimension = 'calls' | 'receptionist' | 'sms' | 'cost';

const formatValue = (dimension: Dimension, value: number): string =>
  dimension === 'cost' ? `${formatNumber(parseFloat(value.toFixed(2)))}€` : formatNumber(value);

// `rows` porte quatre classements déjà distincts et déjà triés côté backend
// (buildTopSubscriptions, backend/src/helpers/leedflow/overview-model.ts) :
// `byCalls`, `byReceptionist`, `bySms`, `byCost`. Chaque onglet lit SA propre
// liste, jamais un retri local d'une seule liste — un abonnement fort en SMS
// mais absent du top appels n'existerait dans aucune liste unique triée par
// appels, quel que soit le tri qu'on lui applique après coup côté front.
const DIMENSION_KEYS: Record<Dimension, keyof TopSubscriptions> = {
  calls: 'byCalls',
  receptionist: 'byReceptionist',
  sms: 'bySms',
  cost: 'byCost',
};

const AI_RECEPTIONIST_PLATFORM = 'elevenlabs_receptionist';

/**
 * Les badges suivent l'onglet : Appels ne montre que les canaux de l'agent
 * (GSM, VoIP), Réceptionniste IA que le sien. SMS et Coût mélangent les deux,
 * ils gardent tout.
 */
const showsPlatform = (dimension: Dimension, platform: string): boolean => {
  if (dimension === 'calls') return platform !== AI_RECEPTIONIST_PLATFORM;
  if (dimension === 'receptionist') return platform === AI_RECEPTIONIST_PLATFORM;
  return true;
};

/**
 * Les plateformes réellement utilisées, de la plus employée à la moins
 * employée. Un abonnement qui bascule progressivement de la GSM vers la VoIP
 * porte les deux badges — c'est justement ce qu'on veut voir.
 */
const platformsOf = (row: TopSubscriptionEntry, dimension: Dimension): string[] =>
  Object.entries(row.platforms)
    .filter(([platform]) => showsPlatform(dimension, platform))
    .sort((a, b) => b[1] - a[1])
    .map(([platform]) => platform);

/**
 * Certains modèles Android ne sont qu'un code constructeur (« SM-S938B ») :
 * le fabricant devient alors la partie lisible du libellé.
 */
const deviceLabel = (device: NonNullable<TopSubscriptionEntry['device']>): string => {
  if (!device.model) return device.manufacturer ?? '';
  const manufacturer = device.manufacturer ?? '';
  const looksLikeCode = /^[A-Z]{2,}-?[A-Z0-9]+$/.test(device.model);
  return looksLikeCode && manufacturer ? `${manufacturer} ${device.model}` : device.model;
};

/**
 * Ce qui identifie la ligne : la personne, puis son numéro.
 *
 * `name` porte le nom d'abonnement tel qu'il est saisi en base, du genre
 * « 33748808020 - Emprunt direct - Lacondemine Alban » : le numéro d'abord,
 * la personne noyée en fin de chaîne, et la partie utile tronquée dès que la
 * ligne est étroite. On repart donc du titulaire résolu côté backend et du
 * `sender` de l'abonnement.
 *
 * Repli sur `name` quand aucun utilisateur mobile n'est rattaché : mieux vaut
 * l'ancien libellé qu'une ligne anonyme.
 */
const rowLabel = (row: TopSubscriptionEntry): string => {
  if (!row.holder) return row.name;
  return row.sender ? `${row.holder.name} ${formatCallNumber(row.sender)}` : row.holder.name;
};

const TopSubscriptionsPanel: React.FC<TopSubscriptionsPanelProps> = ({ rows, status }) => {
  const { token } = useToken();
  const navigate = useNavigate();

  // Les photos ne partent qu'une fois les classements affichés : elles ne
  // retiennent plus les chiffres.
  const holderUids = useMemo(
    () =>
      status === ContextStatus.SUCCESS
        ? [...rows.byCalls, ...rows.byReceptionist, ...rows.bySms, ...rows.byCost].flatMap((row) => (row.holder?.uid ? [row.holder.uid] : []))
        : [],
    [rows, status],
  );
  const avatars = useOverviewAvatars(holderUids);

  const goToSubscription = (id: string) => navigate(`${SUBSCRIPTIONS_PAGE_PATH}/${id}`);

  const buildTab = (dimension: Dimension, label: string) => {
    const dimensionRows: TopSubscriptionEntry[] = rows[DIMENSION_KEYS[dimension]];
    // Part de chaque ligne dans le total de CE classement (top par
    // `dimension`) : les abonnements hors de ce top n'existent pas dans
    // `dimensionRows`, donc jamais dans le total réel de la plateforme.
    const total = dimensionRows.reduce((sum, row) => sum + row[dimension], 0);

    return {
      key: dimension,
      label,
      children:
        dimensionRows.length === 0 ? (
          <div style={{ color: token.colorTextSecondary, textAlign: 'center', padding: '2rem 0' }}>
            Aucune donnée sur la période
          </div>
        ) : (
          <>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {dimensionRows.length > 1
                ? `Part calculée parmi les ${dimensionRows.length} utilisateurs listés ci-dessous, pas sur l'ensemble de la plateforme.`
                : "Part calculée sur l'unique utilisateur listé ci-dessous, pas sur l'ensemble de la plateforme."}
            </Typography.Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {dimensionRows.map((row) => {
                const value = row[dimension];
                const pct = total > 0 ? (value / total) * 100 : 0;
                return (
                  <button
                    key={row.id}
                    type="button"
                    aria-label={`Voir la fiche de ${rowLabel(row)}`}
                    onClick={() => goToSubscription(row.id)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      display: 'block',
                      width: '100%',
                      boxSizing: 'border-box',
                      position: 'relative',
                      borderRadius: token.borderRadiusSM,
                      overflow: 'hidden',
                      padding: '6px 10px',
                      background: token.colorFillTertiary,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: `${pct}%`,
                        background: token.colorPrimaryBg,
                      }}
                    />
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {/* La photo en tête de ligne : c'est le repère le plus
                            rapide dans une liste de dix personnes, avant même
                            le nom. `minWidth: 0` sur le conteneur souple, sinon
                            l'ellipse du nom ne se déclenche jamais et c'est le
                            chiffre du classement qui se fait pousser dehors. */}
                        <Avatar
                          size={28}
                          src={(row.holder && avatars[row.holder.uid]) || undefined}
                          icon={<UserOutlined />}
                          style={{ flexShrink: 0 }}
                        />
                        {/* Le numéro en gris et espacé plutôt que séparé par
                            un tiret : la nuance suffit à dire que c'est une
                            seconde information, sans poser un signe entre les
                            deux. */}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.holder ? row.holder.name : row.name}
                          {row.holder && row.sender && (
                            <span style={{ marginLeft: 8, color: token.colorTextSecondary }}>
                              {formatCallNumber(row.sender)}
                            </span>
                          )}
                        </span>
                      </span>
                      {/* Le chiffre du classement porte la ligne : c'est lui
                          qu'on parcourt de haut en bas. La part relative reste
                          en retrait, elle ne se lit qu'une fois le rang établi. */}
                      <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                        <span
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: token.colorPrimary,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatValue(dimension, value)}
                        </span>
                        <span style={{ color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
                          {pct.toFixed(1)}%
                        </span>
                      </span>
                    </div>

                    {/* Seconde ligne d'identification, sur le modèle du tableau
                        de parcours : ce qui distingue un utilisateur d'un autre
                        au premier coup d'oeil, pas ce qui le classe. */}
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 10,
                        marginTop: 4,
                        fontSize: 12,
                        color: token.colorTextSecondary,
                      }}
                    >
                      {platformsOf(row, dimension).map((platform) => (
                        <span key={platform}>{renderPlatform(platform)}</span>
                      ))}

                      {row.device && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {renderDevice(row.device.platform)}
                          <span>{deviceLabel(row.device)}</span>
                        </span>
                      )}

                      {row.device?.appVersion && <span>v{row.device.appVersion}</span>}

                      {row.lastActivity && <span>{renderDate(row.lastActivity)}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ),
    };
  };

  const isEmpty =
    rows.byCalls.length === 0 &&
    rows.byReceptionist.length === 0 &&
    rows.bySms.length === 0 &&
    rows.byCost.length === 0;

  return (
    <Card title="Meilleurs utilisateurs">
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : isEmpty ? (
        <div style={{ color: token.colorTextSecondary, textAlign: 'center', padding: '2rem 0' }}>
          Aucune donnée sur la période
        </div>
      ) : (
        <Tabs
          size="small"
          items={[
            buildTab('calls', 'Appels'),
            buildTab('receptionist', 'Réceptionniste IA'),
            buildTab('sms', 'SMS'),
            buildTab('cost', 'Coût'),
          ]}
        />
      )}
    </Card>
  );
};

export default TopSubscriptionsPanel;
